import { h, Teleport } from 'vue'

// Drop-in replacement for the parts of vue-js-modal v1 this app uses:
// <modal name="x" width height minHeight scrollable @before-open>, this.$modal.show/hide(name, params)
const registry = new Map()

function toCss (value, fallback) {
  if (value === undefined || value === null) return fallback
  if (value === 'auto') return 'auto'
  if (typeof value === 'number') return value + 'px'
  return /^\d+(\.\d+)?$/.test(String(value)) ? value + 'px' : String(value)
}

const Modal = {
  name: 'modal',
  inheritAttrs: false,
  props: {
    name: { type: String, required: true },
    width: { type: [String, Number], default: 600 },
    height: { type: [String, Number], default: 300 },
    minWidth: { type: Number, default: 0 },
    minHeight: { type: Number, default: 0 },
    scrollable: { type: Boolean, default: false },
    clickToClose: { type: Boolean, default: true }
  },
  emits: ['before-open', 'opened', 'before-close', 'closed'],
  data () {
    return { visible: false }
  },
  created () {
    if (!registry.has(this.name)) registry.set(this.name, new Set())
    registry.get(this.name).add(this)
  },
  beforeUnmount () {
    const set = registry.get(this.name)
    if (set) { set.delete(this); if (!set.size) registry.delete(this.name) }
    document.removeEventListener('keydown', this.onKey)
  },
  methods: {
    show (params) {
      let stopped = false
      this.$emit('before-open', { name: this.name, params: params || {}, stop: () => { stopped = true } })
      if (stopped) return
      this.visible = true
      document.addEventListener('keydown', this.onKey)
      this.$nextTick(() => this.$emit('opened', { name: this.name, params }))
    },
    hide (params) {
      if (!this.visible) return
      let stopped = false
      this.$emit('before-close', { name: this.name, params, stop: () => { stopped = true } })
      if (stopped) return
      this.visible = false
      document.removeEventListener('keydown', this.onKey)
      this.$emit('closed', { name: this.name, params })
    },
    onKey (e) {
      if (e.key === 'Escape' && this.clickToClose) this.hide()
    }
  },
  render () {
    if (!this.visible) return null
    const boxStyle = {
      width: toCss(this.width, '600px'),
      maxWidth: '100%',
      height: toCss(this.height, '300px'),
      minHeight: this.minHeight ? this.minHeight + 'px' : undefined,
      minWidth: this.minWidth ? this.minWidth + 'px' : undefined,
      maxHeight: '95vh',
      overflowY: this.scrollable ? 'auto' : 'visible',
      background: '#fff',
      borderRadius: '3px',
      boxShadow: '0 20px 60px -2px rgba(27, 33, 58, .4)',
      position: 'relative'
    }
    return h(Teleport, { to: 'body' }, [
      h('div', {
        class: 'v--modal-overlay',
        style: {
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh',
          background: 'rgba(0, 0, 0, 0.2)', zIndex: 999,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          overflowY: 'auto', padding: '30px 0', boxSizing: 'border-box'
        },
        onMousedown: e => {
          if (e.target === e.currentTarget && this.clickToClose) this.hide()
        }
      }, [h('div', { class: 'v--modal-box v--modal', style: boxStyle }, this.$slots.default && this.$slots.default())])
    ])
  }
}

function each (name, fn) {
  const set = registry.get(name)
  if (!set) {
    console.warn('[modal] no modal named "' + name + '"')
    return
  }
  ;[...set].forEach(fn)
}

export const modalService = {
  show (name, params) {
    each(name, m => m.show(params))
  },
  hide (name, params) {
    const set = registry.get(name)
    if (set) [...set].forEach(m => m.hide(params))
  },
  toggle (name, params) {
    each(name, m => (m.visible ? m.hide(params) : m.show(params)))
  }
}

export default {
  install (app) {
    app.component('modal', Modal)
    app.config.globalProperties.$modal = modalService
  }
}
